import { useEffect, useState } from 'react';
import { Badge, Button, Sheet, TextArea } from '../components/ui';
import { zh } from '../i18n';
import { formatCountdown } from '../lib/format';
import type { ApprovalItem, ApprovalResolution } from '../stream/streamReducer';

/**
 * The inline approval card (SPEC §10) — never a modal, because a modal over a
 * streaming transcript hides the very context the decision needs.
 *
 * States: pending → (resolving) → resolved receipt. A resolution can also
 * arrive from ANOTHER tab or from the server's own 600 s timeout, in which case
 * the card switches to a receipt it never asked for; that path is why the
 * resolved copy is chosen from `resolution.source` rather than from whatever
 * this card's buttons did.
 */

export interface ApprovalResolveInput {
  readonly decision: 'allow' | 'deny';
  readonly scope: 'once' | 'always';
  readonly note?: string;
}

export interface ApprovalCardProps {
  readonly item: ApprovalItem;
  readonly onResolve: (input: ApprovalResolveInput) => void;
  readonly busy?: boolean;
  /** Injectable clock so the countdown is deterministic in tests. */
  readonly nowMs?: number;
}

function receiptText(resolution: ApprovalResolution, resolvedHere: boolean): string {
  switch (resolution.source) {
    case 'timeout':
      return zh.approval.resolvedTimeout;
    case 'interrupt':
      return zh.approval.resolvedInterrupt;
    case 'shutdown':
      return zh.approval.resolvedShutdown;
    default:
      break;
  }
  const base =
    resolution.decision === 'deny'
      ? zh.approval.resolvedDeny
      : resolution.scope === 'always'
        ? zh.approval.resolvedAllowAlways
        : zh.approval.resolvedAllowOnce;
  return resolvedHere
    ? base
    : zh.approval.resolvedWithSource(base, zh.approval.resolvedElsewhere);
}

function prettyInput(input: unknown): string {
  if (typeof input === 'string') return input;
  try {
    return JSON.stringify(input, null, 2);
  } catch {
    return String(input);
  }
}

export function ApprovalCard({
  item,
  onResolve,
  busy = false,
  nowMs,
}: ApprovalCardProps): JSX.Element {
  const [denying, setDenying] = useState(false);
  const [note, setNote] = useState('');
  const [confirmAlways, setConfirmAlways] = useState(false);
  const [resolvedHere, setResolvedHere] = useState(false);
  const [tick, setTick] = useState(() => nowMs ?? Date.now());

  const settled = item.resolution !== null || item.gone;

  useEffect(() => {
    if (nowMs !== undefined || settled) return;
    const handle = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(handle);
  }, [nowMs, settled]);

  const now = nowMs ?? tick;
  const remainingMs = Math.max(0, item.approval.expiresAt - now);
  const totalMs = Math.max(1, item.approval.expiresAt - item.approval.createdAt);
  const fraction = Math.max(0, Math.min(1, remainingMs / totalMs));

  const resolve = (input: ApprovalResolveInput): void => {
    setResolvedHere(true);
    setDenying(false);
    setConfirmAlways(false);
    onResolve(input);
  };

  if (item.gone) {
    return (
      <div className="approval approval--resolved">
        <div className="approval__head">
          <span className="approval__title">{item.approval.label}</span>
        </div>
        <div className="receipt">{zh.approval.gone}</div>
      </div>
    );
  }

  if (item.resolution) {
    return (
      <div className="approval approval--resolved">
        <div className="approval__head">
          <span className="approval__title">{item.approval.label}</span>
          <span className="small muted">{item.approval.inputSummary}</span>
        </div>
        <div className="receipt">{receiptText(item.resolution, resolvedHere)}</div>
        {item.resolution.note ? (
          <div className="tiny faint">{zh.approval.noteWithText(item.resolution.note)}</div>
        ) : null}
      </div>
    );
  }

  const circumference = 2 * Math.PI * 7;

  return (
    <section className="approval" aria-label={zh.approval.title}>
      <div className="approval__head">
        <span className="approval__title">{zh.approval.title}</span>
        <Badge tone="primary">{item.approval.label}</Badge>
        {item.approval.risk === 'high' ? <Badge tone="danger">{zh.approval.riskHigh}</Badge> : null}
        <span className="spacer" />
        <span className="approval__countdown">
          <svg className="ring" viewBox="0 0 18 18" aria-hidden="true">
            <circle className="ring__track" cx="9" cy="9" r="7" fill="none" strokeWidth="2" />
            <circle
              className="ring__fill"
              cx="9"
              cy="9"
              r="7"
              fill="none"
              strokeWidth="2"
              strokeDasharray={circumference}
              strokeDashoffset={circumference * (1 - fraction)}
              transform="rotate(-90 9 9)"
            />
          </svg>
          {zh.approval.countdown(formatCountdown(remainingMs))}
        </span>
      </div>

      <pre className="tool__pre">{prettyInput(item.approval.input)}</pre>

      {denying ? (
        <div className="stack stack--sm">
          <label className="field__label" htmlFor={`deny-note-${item.approval.id}`}>
            {zh.approval.denyNoteLabel}
          </label>
          <TextArea
            id={`deny-note-${item.approval.id}`}
            value={note}
            maxLength={500}
            onChange={(event) => setNote(event.target.value)}
          />
          <span className="field__hint">{zh.approval.denyNoteHint}</span>
          <div className="approval__actions">
            <Button
              variant="danger"
              disabled={busy}
              onClick={() =>
                resolve({ decision: 'deny', scope: 'once', ...(note ? { note } : {}) })
              }
            >
              {zh.approval.denySubmit}
            </Button>
            <Button variant="ghost" onClick={() => setDenying(false)}>
              {zh.app.cancel}
            </Button>
          </div>
        </div>
      ) : (
        <div className="approval__actions">
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => resolve({ decision: 'allow', scope: 'once' })}
          >
            {busy ? zh.approval.resolving : zh.approval.allowOnce}
          </Button>
          <Button variant="outline" disabled={busy} onClick={() => setConfirmAlways(true)}>
            {zh.approval.allowAlways}
          </Button>
          <Button variant="ghost" disabled={busy} onClick={() => setDenying(true)}>
            {zh.approval.deny}
          </Button>
        </div>
      )}

      {confirmAlways ? (
        <Sheet title={zh.approval.alwaysConfirmTitle} onClose={() => setConfirmAlways(false)}>
          <p className="small">{zh.approval.alwaysConfirmBody(item.approval.label)}</p>
          <div className="approval__actions">
            <Button
              variant="primary"
              onClick={() => resolve({ decision: 'allow', scope: 'always' })}
            >
              {zh.approval.alwaysConfirmOk}
            </Button>
            <Button variant="ghost" onClick={() => setConfirmAlways(false)}>
              {zh.app.cancel}
            </Button>
          </div>
        </Sheet>
      ) : null}
    </section>
  );
}
